import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../api";
import { Star, MapPin, Home, ArrowLeft, Sparkles, MessageSquare, BookOpen, Crown, Lock } from "lucide-react";
import { Button } from "../components/ui/button";

export default function UserPublicCard() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/users/${id}/public-card`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Profil indisponible"));
  }, [id]);

  if (error) return (
    <div className="max-w-md mx-auto px-4 py-12 text-center" data-testid="upc-error">
      <div className="card-soft p-8">
        <div className="text-5xl mb-2">🚫</div>
        <h1 className="font-bold text-xl text-slate-900 mb-1" style={{ fontFamily: "Outfit" }}>Profil non accessible</h1>
        <p className="text-sm text-slate-600">{error}</p>
        <Link to="/messages" className="inline-block mt-4 text-sky-700 hover:underline text-sm">← Retour à la messagerie</Link>
      </div>
    </div>
  );
  if (!data) return <div className="text-center py-12 text-slate-500">Chargement…</div>;

  // For MNS, redirect to the full /mns/{id} richer page (we don't have a fancy client page yet)
  if (data.role === "mns") {
    window.location.replace(`/mns/${data.user_id}`);
    return null;
  }

  const pic = data.picture
    ? (data.picture.startsWith("http") ? data.picture : `${process.env.REACT_APP_BACKEND_URL}${data.picture}`)
    : `https://ui-avatars.com/api/?name=${encodeURIComponent(data.name)}&background=0369A1&color=fff&size=200`;

  const memberSince = data.member_since
    ? new Date(data.member_since).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })
    : null;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="upc-page">
      <Link to="/messages" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-3">
        <ArrowLeft className="w-4 h-4" />Retour
      </Link>
      <div className="card-soft p-6">
        <div className="flex items-center gap-5">
          <img src={pic} alt="" className="w-24 h-24 rounded-full object-cover ring-4 ring-sky-100" />
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-slate-900 truncate" style={{ fontFamily: "Outfit" }}>{data.name}</h1>
            <div className="text-sm text-slate-500 capitalize">{data.role === "client" ? "Élève" : data.role}</div>
            {memberSince && <div className="text-xs text-slate-400 mt-1">Membre depuis {memberSince}</div>}
          </div>
        </div>

        {/* Relationship stats — only meaningful for MNS viewing one of their clients */}
        {(data.bookings_with_you !== undefined) && (
          <div className="grid grid-cols-2 gap-3 mt-6">
            <div className="bg-sky-50 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-sky-700 tabular-nums">{data.bookings_with_you}</div>
              <div className="text-xs uppercase tracking-wide text-slate-500 mt-1">cours réservés avec vous</div>
            </div>
            <div className="bg-emerald-50 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-emerald-700 tabular-nums">{data.completed_with_you}</div>
              <div className="text-xs uppercase tracking-wide text-slate-500 mt-1">cours réalisés</div>
            </div>
          </div>
        )}

        <div className="mt-6 bg-slate-50 rounded-xl p-3 text-xs text-slate-600">
          🔒 Pour respecter la vie privée des élèves, seules les informations strictement nécessaires sont affichées sur ce profil. Pour échanger plus, utilisez la <Link to={`/messages/${data.user_id}`} className="text-sky-700 underline">messagerie</Link>.
        </div>

        <div className="mt-5 flex gap-2">
          <Link to={`/messages/${data.user_id}`}>
            <Button className="rounded-full bg-sky-700 hover:bg-sky-600" data-testid="upc-message-btn">
              <MessageSquare className="w-4 h-4 mr-1" />Envoyer un message
            </Button>
          </Link>
        </div>
      </div>

      {/* Iter35 patch3: pedagogical follow-up — visible only when caller is the MNS of this client */}
      {data.pedagogical !== undefined && (
        <PedagogicalSection
          clientId={data.user_id}
          premiumActive={!!data.premium_active}
          summary={data.pedagogical}
        />
      )}
    </div>
  );
}

function PedagogicalSection({ clientId, premiumActive, summary }) {
  if (!premiumActive) {
    return (
      <div className="card-soft p-6 mt-4 relative overflow-hidden bg-gradient-to-br from-amber-50 to-sky-50 border-2 border-amber-200" data-testid="upc-pedagogical-paywall">
        <div className="absolute top-3 right-3 inline-flex items-center gap-1 bg-amber-500 text-white text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full shadow">
          <Crown className="w-3 h-3" />Premium
        </div>
        <div className="opacity-60 pointer-events-none select-none filter blur-[2px]">
          <h3 className="font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <BookOpen className="w-5 h-5 text-sky-700" />Suivi pédagogique
          </h3>
          <ul className="mt-3 space-y-2 text-sm text-slate-700">
            <li>• Travail sur la respiration crawl, exercice avec bouchons.</li>
            <li>• Bons progrès sur les jambes — démarrage du dos crawlé.</li>
            <li>• Plongeon départ : à retravailler la semaine prochaine.</li>
          </ul>
        </div>
        <div className="relative mt-4 flex items-start gap-3">
          <Lock className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-amber-900">Cette section est réservée aux abonnés Premium</div>
            <p className="text-sm text-amber-800 mt-1">
              Le suivi pédagogique privé (notes + photos par leçon, partage facultatif avec l'élève) fait partie de l'onglet <strong>Clients</strong> Premium.
              <br />Vos 3 mois d'essai démarrent automatiquement à votre 1ère réservation payée.
            </p>
            <Link to="/profile?tab=subscription">
              <Button className="rounded-full bg-amber-600 hover:bg-amber-500 text-white mt-3" data-testid="upc-pedagogical-cta">
                Voir les formules Premium →
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const { total = 0, recent = [] } = summary || {};
  return (
    <div className="card-soft p-6 mt-4" data-testid="upc-pedagogical-active">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <BookOpen className="w-5 h-5 text-sky-700" />Suivi pédagogique
          <span className="text-sm font-normal text-slate-500">({total} note{total > 1 ? "s" : ""})</span>
        </h3>
        <Link to={`/mns/dashboard?tab=clients&client=${clientId}`} className="text-sm text-sky-700 hover:underline" data-testid="upc-pedagogical-open-full">
          Ouvrir la fiche complète →
        </Link>
      </div>

      {recent.length === 0 ? (
        <div className="text-sm text-slate-500 mt-3 border-2 border-dashed border-slate-200 rounded-xl py-6 text-center">
          Aucune note pédagogique pour cet élève. Ajoutez-en depuis la <Link to={`/mns/dashboard?tab=clients&client=${clientId}`} className="text-sky-700 underline">fiche complète</Link>.
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {recent.map((n) => (
            <div key={n.note_id} className="bg-slate-50 rounded-xl p-3" data-testid={`upc-ped-note-${n.note_id}`}>
              <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                <span>Leçon du {new Date(n.lesson_date).toLocaleDateString("fr-FR")}</span>
                {n.share_with_client && (
                  <span className="bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5 text-[10px] font-semibold">
                    📖 Partagée
                  </span>
                )}
              </div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap mt-1">{n.text}</div>
              {n.photos?.length > 0 && (
                <div className="flex gap-1.5 mt-2">
                  {n.photos.slice(0, 4).map((src) => (
                    <img key={src} src={`${process.env.REACT_APP_BACKEND_URL}${src}`} alt="" className="w-12 h-12 rounded-lg object-cover ring-1 ring-slate-200" />
                  ))}
                </div>
              )}
            </div>
          ))}
          {total > recent.length && (
            <div className="text-center pt-2">
              <Link to={`/mns/dashboard?tab=clients&client=${clientId}`} className="text-sm text-sky-700 hover:underline" data-testid="upc-ped-see-all">
                Voir les {total - recent.length} note{total - recent.length > 1 ? "s" : ""} restante{total - recent.length > 1 ? "s" : ""} →
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
